import { useCallback, useEffect, useMemo } from 'react';
import {
  JustifiRegisterTerminalComponents,
  RegisterTerminalModalJustifiSteps,
  JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES,
  RegisterTerminalModalStripeSteps,
} from '@frontend/payments-register-terminal';
import { useMultiStepControl } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { RegisterTerminalNavigationSwitch } from './register-terminal-navigation-switch';
import { RegisterTerminalStepBodyProps } from './register-terminal.types';
import { RegisterTerminalStepper } from './stepper/register-terminal-stepper';

export const RegisterTerminalJustifi = () => {
  return <RegisterTerminalNavigationSwitch body={RegisterTerminalJustifiBody} />;
};

const RegisterTerminalJustifiBody = ({
  onGoBack,
  onCancel,
  onComplete,
  onRegisterTerminal,
}: RegisterTerminalStepBodyProps) => {
  const { activeLocationId, setMinWidth, resetMinWidth } = useCollectPaymentMultiContext();
  const multiStepControl = useMultiStepControl({
    onComplete: () => {
      onComplete();
    },
    onCancel: () => {
      onCancel();
    },
  });

  const childGoBack = useCallback(() => {
    multiStepControl.goPrevStep();
  }, [multiStepControl.goPrevStep]);

  const goToChildStep = useCallback(
    (step: RegisterTerminalModalJustifiSteps | RegisterTerminalModalStripeSteps) => {
      multiStepControl.goToStep({ orStep: step });
    },
    [multiStepControl.goToStep]
  );

  useEffect(() => {
    setMinWidth(776);

    return () => {
      resetMinWidth();
    };
  }, []);

  const steps = useMemo(() => {
    return [
      {
        id: RegisterTerminalModalJustifiSteps.PreCheck,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.PreCheck].heading,
        stepperLabel: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.PreCheck].title,
        component: <JustifiRegisterTerminalComponents.PreCheck goBack={onGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalJustifiSteps.TurnOnTerminal,
        parentId: RegisterTerminalModalJustifiSteps.PreCheck,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.TurnOnTerminal].heading,
        stepperLabel: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.TurnOnTerminal].title,
        component: <JustifiRegisterTerminalComponents.PlugInTerminal goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalJustifiSteps.ConnectToWifiStep1,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.ConnectToWifiStep1].heading,
        stepperLabel:
          JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.ConnectToWifiStep1].title,
        component: (
          <JustifiRegisterTerminalComponents.ConnectToWifiStep1 goBack={childGoBack} goToStep={goToChildStep} />
        ),
      },
      {
        id: RegisterTerminalModalJustifiSteps.ConnectToWifiStep2,
        parentId: RegisterTerminalModalJustifiSteps.ConnectToWifiStep1,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.ConnectToWifiStep2].heading,
        stepperLabel:
          JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.ConnectToWifiStep2].title,
        component: (
          <JustifiRegisterTerminalComponents.ConnectToWifiStep2 goBack={childGoBack} goToStep={goToChildStep} />
        ),
      },
      {
        id: RegisterTerminalModalJustifiSteps.ConnectToWifiStep3,
        parentId: RegisterTerminalModalJustifiSteps.ConnectToWifiStep2,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.ConnectToWifiStep3].heading,
        stepperLabel:
          JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.ConnectToWifiStep3].title,
        component: (
          <JustifiRegisterTerminalComponents.ConnectToWifiStep3 goBack={childGoBack} goToStep={goToChildStep} />
        ),
      },
      {
        id: RegisterTerminalModalJustifiSteps.IncorrectPassword,
        parentId: RegisterTerminalModalJustifiSteps.ConnectToWifiStep3,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.IncorrectPassword].heading,
        stepperLabel: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.IncorrectPassword].title,
        component: (
          <JustifiRegisterTerminalComponents.IncorrectPassword goBack={childGoBack} goToStep={goToChildStep} />
        ),
      },
      {
        id: RegisterTerminalModalJustifiSteps.GenrateDeviceId,
        title: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.GenrateDeviceId].heading,
        stepperLabel: JUSTIFI_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalJustifiSteps.GenrateDeviceId].title,
        component: (
          <JustifiRegisterTerminalComponents.GenrateDeviceId
            goBack={childGoBack}
            goToStep={goToChildStep}
            onSuccess={onComplete}
            onTerminalPaired={onRegisterTerminal}
            locationId={activeLocationId}
          />
        ),
      },
    ];
  }, [onRegisterTerminal, childGoBack, onGoBack, goToChildStep, onComplete]);

  return <RegisterTerminalStepper steps={steps} multiStepControl={multiStepControl} />;
};
